import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { concatLatestFrom } from '@ngrx/operators'
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators'
import { Store } from '@ngrx/store'
import { mapMatchMetricDetailed, mapMetricDetailed } from '../../utils/metric-detail.utils'
import { EMPTY, Observable } from 'rxjs'
import { fromPromise } from 'rxjs/internal/observable/innerFrom'
import { McmModalControllerService } from '@core/services/mcm-modal-controller/mcm-modal-controller.service'
import {
  closeMetricDetailModal,
  fetchMetricDetail,
  openMetricDetailModal,
  purgeMetricDetail,
  setMetricDetailContext,
  setMetricDetailItems,
  setMetricDetailLoader,
  setMetricDetailSourceMetric
} from '@features/metric-detail/store/actions/metric-detail.actions'
import { ContextMenuHolderWrapper, ContextMenuItemEvent } from '@core/models/context-menu.models'
import { MetricRequest } from '@core/models/metric.models'
import { MetricFinderKey } from '@core/enums/metric.enums'
import { DisplayMode } from '@core/enums/display.enums'
import { resolveTeamMetricDetailRequestAdapter } from '@features/metric-detail/adapters/team-metric-detail.adapter'
import { resolvePlayerMetricDetailRequestAdapter } from '@features/metric-detail/adapters/player-metric-detail.adapter'
import { resolveMatchMetricDetailRequest } from '@features/metric-detail/adapters/match-metric-detail.adapter'
import { MetricDetailApi } from '@features/metric-detail/api/metric-detail.api'
import { MetricDetail, MetricDetailContext, MetricDetailed } from '@features/metric-detail/models/metric-detail.models'
import { sortBy } from 'lodash-es'
import { METRIC_DETAIL_SORT_WEIGHT } from '@features/metric-detail/constants/metric-detail.constants'
import { MetricDetailContainerComponent } from '@features/metric-detail/metric-detail-container.component'
import {
  selectAggregationContextMetadata,
  selectAggregationsContext
} from '@features/aggregations/store/selectors/aggregations.selectors'
import { Aggregation, AggregationContext } from '@features/aggregations/models/aggregations.models'
import { selectMergedRouteData } from '@core/ngrx-router/state/selectors/ngrx-router.selectors'
import { CallingContext } from '@features/seasons/models/seasons.models'

@Injectable()
export class MetricDetailEffects {
  private readonly _actions: Actions = inject(Actions)
  private readonly _store: Store = inject(Store)
  private readonly _api: MetricDetailApi = inject(MetricDetailApi)
  private _modalController: McmModalControllerService = inject(McmModalControllerService)

  private _modal: HTMLIonModalElement

  fetchMetricDetail$ = createEffect(() =>
    this._actions.pipe(
      ofType(fetchMetricDetail),
      concatLatestFrom(() => [
        this._store.select(selectAggregationsContext),
        this._store.select(selectAggregationContextMetadata),
        this._store.select(selectMergedRouteData)
      ]),
      map(([{ payload }, context, aggregationMetadata, routeData]) => ({
        originalPayload: payload,
        resolvedPayload: this._resolvePayload(payload, context, aggregationMetadata),
        routeData
      })),
      tap(({ resolvedPayload, routeData }) => {
        this._store.dispatch(setMetricDetailLoader({ loading: true }))
        this._store.dispatch(setMetricDetailContext({ ...resolvedPayload }))
        this._store.dispatch(
          openMetricDetailModal({
            payload: { ...resolvedPayload, callingContext: routeData['callingContext'] as CallingContext }
          })
        )
      }),
      switchMap((payload) =>
        this._fetchMetricDetail(payload).pipe(
          catchError((err) => {
            this._store.dispatch(setMetricDetailLoader({ loading: false }))
            console.error(err)
            return EMPTY
          })
        )
      ),
      mergeMap(({ originalPayload, resolvedPayload: payload, items }) => [
        setMetricDetailSourceMetric({ sourceMetric: originalPayload.value }),
        setMetricDetailLoader({ loading: false }),
        setMetricDetailItems({
          items: this._filterAndSortMetricDetailedItems(
            items,
            (payload.metricContext as ContextMenuHolderWrapper)?.overrideAggregationMode
          )
        })
      ])
    )
  )

  openMetricDetailModal$ = createEffect(
    () =>
      this._actions.pipe(
        ofType(openMetricDetailModal),
        switchMap(({ payload }) => this._createMetricDetailModal(payload)),
        switchMap((modal) => {
          this._modal = modal
          return fromPromise(this._modal.present())
        })
      ),
    { dispatch: false }
  )

  closeMetricDetailModal$ = createEffect(() =>
    this._actions.pipe(
      ofType(closeMetricDetailModal),
      switchMap(() => fromPromise(this._modal?.dismiss())),
      map(() => purgeMetricDetail())
    )
  )

  private _createMetricDetailModal(componentProps: MetricDetailContext): Observable<HTMLIonModalElement> {
    return this._modalController.createObs({
      component: MetricDetailContainerComponent,
      componentProps,
      cssClass: 'mcm-metric-detail',
      backdropDismiss: true
    })
  }

  private _resolvePayload(
    payload: ContextMenuItemEvent,
    context: MetricFinderKey,
    contextMetadata: AggregationContext
  ): MetricDetailContext {
    let metricToDetailed: MetricRequest
    let displayMode: DisplayMode

    switch (context) {
      case MetricFinderKey.Team:
        metricToDetailed = resolveTeamMetricDetailRequestAdapter(payload, contextMetadata)
        displayMode = contextMetadata.isVsEnabled ? DisplayMode.ComparedTeam : DisplayMode.Team
        break
      case MetricFinderKey.Player:
        metricToDetailed = resolvePlayerMetricDetailRequestAdapter(payload, contextMetadata)
        displayMode = contextMetadata.isVsEnabled
          ? contextMetadata.isMatch
            ? DisplayMode.MatchComparedPlayer
            : DisplayMode.ComparedPlayer
          : contextMetadata.isMatch
            ? DisplayMode.MatchPlayer
            : DisplayMode.Player
        break
      case MetricFinderKey.Match:
        metricToDetailed = resolveMatchMetricDetailRequest(payload)
        displayMode = DisplayMode.Match
        break
    }
    return {
      metricToDetailed,
      displayMode,
      metricContext: payload.valueContext
    }
  }

  private _fetchMetricDetail(payload: {
    originalPayload: ContextMenuItemEvent
    resolvedPayload: MetricDetailContext
  }): Observable<{
    originalPayload: ContextMenuItemEvent
    resolvedPayload: MetricDetailContext
    items: MetricDetailed[]
  }> {
    const { originalPayload, resolvedPayload } = payload
    const { displayMode, metricToDetailed } = resolvedPayload

    if (displayMode === DisplayMode.Match) {
      return this._api
        .fetchMatchTeamMetricDetail(metricToDetailed)
        .pipe(
          map((metricDetailed) => ({ originalPayload, resolvedPayload, items: mapMatchMetricDetailed(metricDetailed) }))
        )
    }

    let apiCall$: Observable<MetricDetail>
    switch (displayMode) {
      case DisplayMode.Team:
      case DisplayMode.ComparedTeam:
        apiCall$ = this._api.fetchTeamMetricDetail(metricToDetailed)
        break
      case DisplayMode.Player:
      case DisplayMode.ComparedPlayer:
        apiCall$ = this._api.fetchPlayerMetricDetail(metricToDetailed)
        break
      case DisplayMode.MatchPlayer:
      case DisplayMode.MatchComparedPlayer:
        apiCall$ = this._api.fetchMatchPlayerMetricDetail(metricToDetailed)
        break
    }

    return apiCall$.pipe(
      map((metric) => ({
        originalPayload,
        resolvedPayload,
        items: mapMetricDetailed(metric, metricToDetailed.playerPosition, displayMode)
      }))
    )
  }

  private _filterAndSortMetricDetailedItems(items: MetricDetailed[], overrideAggregationMode: Aggregation) {
    return sortBy(
      overrideAggregationMode ? items.filter(({ id }) => id !== 'metric') : items,
      ({ id }) => METRIC_DETAIL_SORT_WEIGHT[id]
    )
  }
}
